// Productie Breakdown — per productie bijhouden wat er geregeld moet worden (cast, locaties,
// props, voertuigen, wardrobe, SFX), met scenes, shotlist, stripboard, planning, crew,
// materiaal, budget, AI-scriptanalyse en exports.
// Werkpagina: links de producties (tool-eigen kolom), rechts de productie in tabbladen.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Autocomplete, Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, LinearProgress, Menu, MenuItem, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PageHeader from '../../ui-components/PageHeader';
import { bevestig } from '../../utils/bevestig';
import { registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { meldFout } from '../../utils/apiFout';
import ItemDialoog from './ItemDialoog';
import BreakdownTab from './tabs/BreakdownTab';
import ScenesTab from './tabs/ScenesTab';
import ShotlistTab from './tabs/ShotlistTab';
import StripboardTab from './tabs/StripboardTab';
import PlanningTab from './tabs/PlanningTab';
import CrewTab from './tabs/CrewTab';
import MateriaalTab from './tabs/MateriaalTab';
import BudgetTab from './tabs/BudgetTab';
import ScriptTab from './tabs/ScriptTab';
import ExportsTab from './tabs/ExportsTab';
import LogTab from './tabs/LogTab';
import {
  api, haalProductie, lijstCrew, lijstItems, lijstMateriaal, lijstProducties, lijstScenes, lijstShots,
  nieuweProductie, overzicht, wijzigProductie, zoekProjecten, ACCENT, INKT, KOLOM_BG, LIJN, STIL,
} from './breakdownApi';

const TABS = [
  ['breakdown', 'Breakdown'], ['scenes', 'Scenes'], ['shotlist', 'Shotlist'], ['stripboard', 'Stripboard'],
  ['planning', 'Planning'], ['crew', 'Crew'], ['materiaal', 'Materiaal'], ['budget', 'Budget'],
  ['script', 'Script (AI)'], ['export', 'Export'], ['log', 'Log'],
];
const LEEG_FORM = { project: null, name: '', client: '', description: '' };
const LEEG_DATA = { items: [], scenes: [], shots: [], crew: [], materiaal: [] };

function ProductieRij({ p, telling, actief, onClick }) {
  const t = telling || { open: 0, toegezegd: 0, bevestigd: 0, te_laat: 0 };
  const totaal = t.open + t.toegezegd + t.bevestigd;
  const pct = totaal ? Math.round((t.bevestigd / totaal) * 100) : 0;
  return (
    <ButtonBase onClick={onClick} sx={{ display: 'block', width: '100%', textAlign: 'left', px: 1.25, py: 1, borderRadius: 1.5,
      bgcolor: actief ? '#eceff3' : 'transparent', '&:hover': { bgcolor: actief ? '#eceff3' : '#f3f4f6' } }}>
      <Typography noWrap sx={{ fontSize: 13.5, fontWeight: actief ? 700 : 500, color: INKT }}>{p.name}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
        <Typography noWrap sx={{ flex: 1, fontSize: 12, color: STIL }}>{[p.project_number, p.client].filter(Boolean).join(' · ') || '—'}</Typography>
        {t.te_laat > 0 && <Typography sx={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 700 }}>{t.te_laat} te laat</Typography>}
        <Typography sx={{ fontSize: 11.5, color: STIL }}>{t.bevestigd}/{totaal}</Typography>
      </Box>
      {totaal > 0 && (
        <LinearProgress variant="determinate" value={pct}
          sx={{ mt: 0.5, height: 3, borderRadius: 2, bgcolor: LIJN, '& .MuiLinearProgress-bar': { bgcolor: '#15803d' } }} />
      )}
    </ButtonBase>
  );
}

function Overzicht({ producties, tellingen, onKies }) {
  const leeg = { open: 0, toegezegd: 0, bevestigd: 0, te_laat: 0 };
  const rijen = producties.map((p) => ({ p, t: tellingen[p.id] || leeg }))
    .sort((a, b) => b.t.te_laat - a.t.te_laat || (b.t.open + b.t.toegezegd) - (a.t.open + a.t.toegezegd));
  const totaal = rijen.reduce((s, { t }) => ({ open: s.open + t.open, toegezegd: s.toegezegd + t.toegezegd, te_laat: s.te_laat + t.te_laat }),
    { open: 0, toegezegd: 0, te_laat: 0 });
  const getal = (n, label, kleur) => (
    <Box sx={{ flex: '1 1 140px', border: `1px solid ${LIJN}`, borderRadius: 2, px: 2, py: 1.5 }}>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color: kleur || INKT }}>{n}</Typography>
      <Typography sx={{ fontSize: 12.5, color: STIL }}>{label}</Typography>
    </Box>
  );
  return (
    <Box sx={{ maxWidth: 1080, mx: 'auto' }}>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: INKT, mb: 1.5 }}>Overzicht</Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
        {getal(producties.length, 'producties')}
        {getal(totaal.open, 'nog te regelen')}
        {getal(totaal.toegezegd, 'toegezegd', '#b45309')}
        {getal(totaal.te_laat, 'over de deadline', totaal.te_laat ? '#b91c1c' : undefined)}
      </Box>
      <Box sx={{ border: `1px solid ${LIJN}`, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: '2fr 1fr 80px 80px 80px 80px', gap: 1, px: 1.5, py: 1,
          bgcolor: KOLOM_BG, fontSize: 11.5, fontWeight: 700, color: STIL, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          <span>Productie</span><span>Project</span><span>Open</span><span>Toegezegd</span><span>Rond</span><span>Te laat</span>
        </Box>
        {rijen.map(({ p, t }) => (
          <ButtonBase key={p.id} onClick={() => onKies(p.id)} sx={{ display: 'grid', width: '100%', textAlign: 'left',
            gridTemplateColumns: { xs: '1fr auto', sm: '2fr 1fr 80px 80px 80px 80px' }, gap: 1, px: 1.5, py: 1.1,
            borderTop: `1px solid ${LIJN}`, fontSize: 13.5, '&:hover': { bgcolor: '#fafbfc' } }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: INKT }}>{p.name}</Typography>
              {p.client && <Typography noWrap sx={{ fontSize: 12, color: STIL }}>{p.client}</Typography>}
            </Box>
            <Typography noWrap sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 13, color: STIL }}>{p.project_number || '—'}</Typography>
            <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 13.5 }}>{t.open}</Typography>
            <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 13.5 }}>{t.toegezegd}</Typography>
            <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 13.5, color: '#15803d' }}>{t.bevestigd}</Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: t.te_laat ? 700 : 400, color: t.te_laat ? '#b91c1c' : STIL }}>
              {t.te_laat || '—'}<Box component="span" sx={{ display: { sm: 'none' } }}>{t.te_laat ? ' te laat' : ''}</Box>
            </Typography>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}

function ProjectKiezer({ waarde, onKies }) {
  const [invoer, setInvoer] = useState('');
  const [opties, setOpties] = useState([]);
  const [laden, setLaden] = useState(false);
  useEffect(() => {
    if (invoer.trim().length < 2) { setOpties([]); return undefined; }
    let weg = false;
    setLaden(true);
    const t = setTimeout(() => {
      zoekProjecten(invoer.trim())
        .then((r) => { if (!weg) setOpties(r); })
        .catch((e) => meldFout(e, 'Projecten zoeken'))
        .finally(() => { if (!weg) setLaden(false); });
    }, 250);
    return () => { weg = true; clearTimeout(t); };
  }, [invoer]);
  return (
    <Autocomplete value={waarde} options={waarde ? [waarde, ...opties.filter((o) => o.project_number !== waarde.project_number)] : opties}
      filterOptions={(x) => x} loading={laden} onChange={(_, v) => onKies(v)} onInputChange={(_, v) => setInvoer(v)}
      getOptionLabel={(o) => `${o.project_number} · ${o.project_name}`}
      isOptionEqualToValue={(a, b) => a.project_number === b.project_number}
      noOptionsText={invoer.trim().length < 2 ? 'Typ minstens 2 tekens' : 'Geen project gevonden'}
      renderOption={(props, o) => (
        <li {...props} key={o.project_number}>
          <Box>
            <Typography sx={{ fontSize: 13.5 }}><b>{o.project_number}</b> · {o.project_name}</Typography>
            {o.client && <Typography sx={{ fontSize: 12, color: STIL }}>{o.client}</Typography>}
          </Box>
        </li>
      )}
      renderInput={(params) => <TextField {...params} size="small" label="Project (nummer of naam)" autoFocus />} />
  );
}

export default function ProductieBreakdown() {
  const { productieId } = useParams();
  const navigate = useNavigate();
  const [zoekParams, setZoekParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === zoekParams.get('tab')) ? zoekParams.get('tab') : 'breakdown';
  const [producties, setProducties] = useState(null);
  const [tellingen, setTellingen] = useState({});
  const [productie, setProductie] = useState(null);
  const [data, setData] = useState(LEEG_DATA);
  const [laden, setLaden] = useState(false);
  const [openItemId, setOpenItemId] = useState(null);
  const [formOpen, setFormOpen] = useState(null); // null | 'nieuw' | 'wijzig'
  const [form, setForm] = useState(LEEG_FORM);
  const [meerAnker, setMeerAnker] = useState(null);
  const [bewaren, setBewaren] = useState(false);
  const bewarenRef = useRef(false); // synchroon slot tegen dubbelklik/Enter vóór de re-render
  const actueel = useRef(productieId);
  actueel.current = productieId;

  useEffect(() => registreerOngedaanSneltoets(), []);

  const laadLijst = useCallback(async () => {
    try {
      const [lijst, o] = await Promise.all([lijstProducties(), overzicht()]);
      setProducties(lijst);
      setTellingen(o.per_productie || {});
    } catch (e) { meldFout(e, 'Producties laden'); setProducties((p) => p || []); }
  }, []);
  useEffect(() => { laadLijst(); }, [laadLijst]);

  const laadProductie = useCallback(async (id, stil = false) => {
    if (!stil) setLaden(true);
    try {
      const [p, items, scenes, shots, crew, materiaal] = await Promise.all([
        haalProductie(id), lijstItems(id), lijstScenes(id), lijstShots(id), lijstCrew(id), lijstMateriaal(id)]);
      if (actueel.current !== id) return; // intussen gewisseld: dit antwoord is oud
      setProductie(p);
      setData({ items, scenes, shots, crew, materiaal });
    } catch (e) {
      if (actueel.current !== id) return;
      meldFout(e, 'Productie laden');
      if (!stil) setProductie(null);
    } finally { if (actueel.current === id) setLaden(false); }
  }, []);

  useEffect(() => {
    setOpenItemId(null);
    setData(LEEG_DATA);
    setProductie(null);
    if (productieId) laadProductie(productieId);
  }, [productieId, laadProductie]);

  // Tellingen in de linkerkolom live meenemen met de items op het scherm.
  useEffect(() => {
    if (!productie) return;
    const t = { open: 0, toegezegd: 0, bevestigd: 0, te_laat: 0 };
    const vandaag = new Date().toISOString().slice(0, 10);
    data.items.forEach((i) => { t[i.status] += 1; if (i.due_date && i.status !== 'bevestigd' && i.due_date < vandaag) t.te_laat += 1; });
    setTellingen((alle) => ({ ...alle, [productie.id]: t }));
  }, [data.items, productie]);

  // Antwoorden horen bij een productie: is er intussen gewisseld, dan negeren.
  const zet = useCallback((sleutel, waarde, productieIdVanAntwoord) => {
    if (productieIdVanAntwoord && productieIdVanAntwoord !== actueel.current) return;
    setData((d) => ({ ...d, [sleutel]: typeof waarde === 'function' ? waarde(d[sleutel]) : waarde }));
  }, []);
  const setter = (sleutel) => (waarde) => zet(sleutel, waarde, productie?.id);
  const vervangItem = useCallback((nieuw) => zet('items', (lijst) => lijst.map((i) => (i.id === nieuw.id ? nieuw : i)), nieuw.production_id), [zet]);
  const itemErbij = useCallback((nieuw) => zet('items', (lijst) => [...lijst, nieuw], nieuw.production_id), [zet]);
  const itemWeg = useCallback((id, productieIdVanItem) => {
    setOpenItemId((huidig) => (huidig === id ? null : huidig));
    zet('items', (lijst) => lijst.filter((i) => i.id !== id), productieIdVanItem);
  }, [zet]);

  function kiesTab(k) {
    const p = new URLSearchParams(zoekParams);
    if (k === 'breakdown') p.delete('tab'); else p.set('tab', k);
    setZoekParams(p, { replace: true });
  }

  function openForm(soort) {
    setMeerAnker(null);
    setForm(soort === 'wijzig' && productie ? {
      project: productie.project_number ? { project_number: productie.project_number, project_name: productie.project_name || '', client: '' } : null,
      name: productie.name, client: productie.client, description: productie.description,
    } : LEEG_FORM);
    setFormOpen(soort);
  }

  async function bewaarForm() {
    if (!form.name.trim() || !form.project || bewarenRef.current) return;
    bewarenRef.current = true;
    setBewaren(true);
    const body = { project_number: form.project.project_number, name: form.name, client: form.client, description: form.description };
    try {
      if (formOpen === 'nieuw') {
        const p = await nieuweProductie(body);
        setProducties((lijst) => [p, ...(lijst || [])]);
        navigate(`/productie-breakdown/${p.id}`);
      } else {
        const p = await wijzigProductie(productie.id, body);
        if (actueel.current === p.id) setProductie(p);
        setProducties((lijst) => (lijst || []).map((x) => (x.id === p.id ? p : x)));
      }
      setFormOpen(null);
    } catch (e) { meldFout(e, 'Productie opslaan'); }
    finally { bewarenRef.current = false; setBewaren(false); }
  }

  async function verwijderProductie() {
    setMeerAnker(null);
    const p = productie;
    const ok = await bevestig({
      titel: 'Productie verwijderen?',
      tekst: `"${p.name}" wordt definitief verwijderd, met alle items, opties, documenten, scenes, shots, crew, materiaal, planning en het log.`,
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return;
    try {
      await api(`/productions/${p.id}`, { method: 'DELETE', wat: 'Productie verwijderen' });
      setProducties((lijst) => (lijst || []).filter((x) => x.id !== p.id));
      navigate('/productie-breakdown');
    } catch (e) { meldFout(e, 'Productie verwijderen'); }
  }

  const openItem = data.items.find((i) => i.id === openItemId) || null;
  const herlaad = () => productie && laadProductie(productie.id, true);
  const props = { productie, items: data.items };

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader dense icon="fact_check" title="Productie Breakdown"
        subtitle="Wat moet er per productie geregeld worden — en wat is al rond"
        rightActions={(
          <Button variant="contained" disableElevation size="small" startIcon={<AddIcon />} onClick={() => openForm('nieuw')}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>
            Nieuwe productie
          </Button>
        )} />

      <Box sx={{ display: 'flex', border: `1px solid ${LIJN}`, borderRadius: 3, bgcolor: '#fff', overflow: 'hidden', minHeight: 'calc(100vh - 210px)' }}>
        <Box sx={{ width: 264, flexShrink: 0, bgcolor: KOLOM_BG, borderRight: `1px solid ${LIJN}`, p: 1.25,
          display: { xs: 'none', md: 'block' }, position: 'sticky', top: 0, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>
          <Typography sx={{ px: 1.25, pt: 0.5, pb: 1, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: STIL }}>
            Producties
          </Typography>
          {producties === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={16} /></Box>
          ) : producties.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: STIL, px: 1.25 }}>Nog geen producties.</Typography>
          ) : producties.map((x) => (
            <ProductieRij key={x.id} p={x} telling={tellingen[x.id]} actief={x.id === productieId}
              onClick={() => navigate(`/productie-breakdown/${x.id}`)} />
          ))}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, px: { xs: 2, md: 5 }, py: { xs: 2.5, md: 4 } }}>
          {!productieId && producties?.length > 0 ? (
            <Overzicht producties={producties} tellingen={tellingen} onKies={(id) => navigate(`/productie-breakdown/${id}`)} />
          ) : !productieId ? (
            <Box sx={{ maxWidth: 560, mx: 'auto', mt: { xs: 3, md: 10 }, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: INKT }}>
                {producties === null ? '' : 'Begin met een productie'}
              </Typography>
              <Typography sx={{ mt: 1, fontSize: 14, color: STIL }}>
                Koppel een productie aan een project en zet alles op een rij wat geregeld moet worden — cast, locaties,
                props, voertuigen, wardrobe en SFX — met voorstellen, contracten, scenes, shotlist, stripboard, crew en
                materiaal. Plak een script en laat de AI de eerste breakdown maken.
              </Typography>
              {producties !== null && (
                <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => openForm('nieuw')}
                  sx={{ mt: 2.5, textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>
                  Nieuwe productie
                </Button>
              )}
            </Box>
          ) : laden || !productie ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              {laden ? <CircularProgress size={24} /> : <Typography sx={{ color: STIL }}>Productie niet gevonden.</Typography>}
            </Box>
          ) : (
            <Box sx={{ maxWidth: 1080, mx: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: INKT }}>{productie.name}</Typography>
                  <Typography sx={{ fontSize: 14, color: STIL }}>
                    {[productie.project_number && `${productie.project_number} · ${productie.project_name || ''}`, productie.client].filter(Boolean).join('  ·  ')}
                  </Typography>
                  {productie.description && <Typography sx={{ mt: 0.75, fontSize: 13.5, color: '#475569', whiteSpace: 'pre-wrap' }}>{productie.description}</Typography>}
                </Box>
                <IconButton size="small" aria-label="Meer" onClick={(e) => setMeerAnker(e.currentTarget)} sx={{ color: STIL }}>
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
                <Menu anchorEl={meerAnker} open={!!meerAnker} onClose={() => setMeerAnker(null)}>
                  <MenuItem onClick={() => openForm('wijzig')} sx={{ fontSize: 13.5 }}>Gegevens wijzigen</MenuItem>
                  <MenuItem onClick={verwijderProductie} sx={{ fontSize: 13.5, color: '#b91c1c' }}>Productie verwijderen…</MenuItem>
                </Menu>
              </Box>

              <Tabs value={tab} onChange={(_, k) => kiesTab(k)} variant="scrollable" scrollButtons="auto"
                sx={{ mb: 3, borderBottom: `1px solid ${LIJN}`, minHeight: 40,
                  '& .MuiTab-root': { textTransform: 'none', fontSize: 13.5, fontWeight: 600, minHeight: 40, px: 1.5, color: STIL },
                  '& .Mui-selected': { color: `${ACCENT} !important` }, '& .MuiTabs-indicator': { bgcolor: ACCENT } }}>
                {TABS.map(([k, label]) => <Tab key={k} value={k} label={label} />)}
              </Tabs>

              {tab === 'breakdown' && <BreakdownTab {...props} onItem={vervangItem} onItemErbij={itemErbij} onItemWeg={itemWeg} onOpen={setOpenItemId} />}
              {tab === 'scenes' && <ScenesTab productie={productie} scenes={data.scenes} setScenes={setter('scenes')} />}
              {tab === 'shotlist' && <ShotlistTab productie={productie} scenes={data.scenes} shots={data.shots} setShots={setter('shots')} herlaad={herlaad} />}
              {tab === 'stripboard' && <StripboardTab productie={productie} scenes={data.scenes} />}
              {tab === 'planning' && <PlanningTab productie={productie} />}
              {tab === 'crew' && <CrewTab productie={productie} crew={data.crew} setCrew={setter('crew')} />}
              {tab === 'materiaal' && <MateriaalTab productie={productie} materiaal={data.materiaal} setMateriaal={setter('materiaal')} />}
              {tab === 'budget' && <BudgetTab {...props} />}
              {tab === 'script' && <ScriptTab productie={productie} herlaad={herlaad} />}
              {tab === 'export' && <ExportsTab productie={productie} onProductie={(p) => { if (actueel.current === p.id) setProductie(p); }} />}
              {tab === 'log' && <LogTab {...props} />}
            </Box>
          )}
        </Box>
      </Box>

      {openItem && (
        <ItemDialoog item={openItem} onClose={() => setOpenItemId(null)} onGewijzigd={vervangItem} onToegevoegd={itemErbij}
          onVerwijderd={itemWeg} />
      )}

      <Dialog open={!!formOpen} onClose={() => { if (!bewaren) setFormOpen(null); }} fullWidth maxWidth="xs">
        <DialogTitle>{formOpen === 'nieuw' ? 'Nieuwe productie' : 'Productie wijzigen'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <ProjectKiezer waarde={form.project} onKies={(project) => setForm((f) => ({
            ...f, project, name: f.name || project?.project_name || '', client: f.client || project?.client || '',
          }))} />
          <TextField label="Naam van de productie" size="small" value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') bewaarForm(); }} />
          <TextField label="Klant" size="small" value={form.client} onChange={(e) => setForm((f) => ({ ...f, client: e.target.value }))} />
          <TextField label="Omschrijving" size="small" multiline minRows={2} value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(null)} sx={{ textTransform: 'none', color: STIL }}>Annuleren</Button>
          <Button variant="contained" disableElevation disabled={!form.name.trim() || !form.project || bewaren} onClick={bewaarForm}
            sx={{ textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>
            {formOpen === 'nieuw' ? 'Aanmaken' : 'Opslaan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
